import React, { useMemo, useState } from "react";
import { Card, Row, Col, Alert, Affix } from "antd";
import { VisualSeoDimension } from "../types";
import LoadingIndicator from "../../components/shared/LoadingIndicator/LoadingIndicator";
import RefreshVisualSeoAnalysisButton from "./RefreshVisualSeoAnalysisButton";
import DownloadPdfButton from "./DownloadPdfButton";
import useWebpageVisualSeoAnalysis from "./hooks/useWebpageVisualSeoAnalysis";
import VisualSeoHeader from "./VisualSeoHeader";
import PriorityFixesCard from "./PriorityFixesCard";
import DimensionCard from "./DimensionCard";
import "./VisualSeoAnalysis.less";

const VISUAL_SEO_DIMENSIONS = [
    "above_the_fold_value_clarity",
    "message_intent_alignment",
    "attention_flow_and_visual_hierarchy",
    "friction_and_cognitive_load",
    "trust_and_credibility",
    "conversion_path_clarity"
];

const DIMENSION_TITLES: Record<string, string> = {
    above_the_fold_value_clarity: "Above-the-Fold Value Clarity",
    message_intent_alignment: "Message-Intent Alignment",
    attention_flow_and_visual_hierarchy: "Attention Flow & Visual Hierarchy",
    friction_and_cognitive_load: "Friction & Cognitive Load",
    trust_and_credibility: "Trust & Credibility",
    conversion_path_clarity: "Conversion Path Clarity",
}

type Props = {
    wGuid: string;
    url: string;
}

export default function VisualSeoAnalysis({ wGuid, url }: Props) {
    const [, setIsRefreshingAnalysis] = useState(false);
    const { data: analysis, isLoading, isInProgress } = useWebpageVisualSeoAnalysis(wGuid, url);

    const content = analysis?.content;
    const showInitialLoading = isLoading && !content;

    const dimensionsByKey = useMemo(() => {
        const map: Record<string, VisualSeoDimension> = {};
        (content?.dimensions || []).forEach((dimension) => {
            map[dimension.key] = dimension;
        });
        return map;
    }, [content]);

    return (
        <div className="visual-seo-analysis">
            {showInitialLoading && <LoadingIndicator />}

            {isInProgress && (
                <Card className="visual-seo-progress-card">
                    <Alert
                        className="analysis-in-progress-alert"
                        type="info"
                        showIcon
                        message={
                            <div className="analysis-progress">
                                <div className="title">Visual SEO Analysis in progress</div>
                                <div className="loader"></div>
                            </div>
                        }
                        description={
                            <>
                                <span className="alert-message">
                                    We are performing a Visual SEO Analysis for your page. This may take a couple of minutes, depending on your page's complexity.&nbsp;
                                </span>
                                <span className="alert-message">
                                    Feel free to leave this page - your analysis will continue running in the background.
                                </span>
                            </>
                        }
                    />
                </Card>
            )}

            {content && analysis && (
                <>
                    <VisualSeoHeader
                        url={analysis.url}
                        checkedAt={analysis.c_at}
                        screenshotUrl={analysis.screenshot_url}
                        overallScore={content.overall_score}
                        executiveSummary={content.executive_summary}
                        dimensionData={content.dimensions.map((dimension) => ({ key: dimension.key, score: dimension.score, dimensionName: DIMENSION_TITLES[dimension.key] }))}
                        extra={
                            <div className="analysis-actions">
                                <RefreshVisualSeoAnalysisButton
                                    wGuid={wGuid}
                                    url={url}
                                    inProgress={isInProgress}
                                    setIsRefreshingAnalysis={setIsRefreshingAnalysis}
                                />
                                <DownloadPdfButton wGuid={wGuid} url={url} disabled={isInProgress} />
                            </div>
                        }
                    />

                    <Row gutter={24} className="visual-seo-body-row">
                        <Col span={18} className="visual-seo-dimensions-col">
                            {VISUAL_SEO_DIMENSIONS.map((dimensionKey) => (
                                <DimensionCard
                                    key={dimensionKey}
                                    dimension={dimensionsByKey[dimensionKey]}
                                />
                            ))}
                        </Col>
                        <Col span={6} className="visual-seo-suggestions-col">
                            <Affix offsetTop={70} className="sticky">
                                <PriorityFixesCard suggestions={content.suggestions || []} />
                            </Affix>
                        </Col>
                    </Row>
                </>
            )}
        </div>
    );
}
